import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, SpinnerGap, Lightning, FloppyDisk, ArrowsClockwise,
  Target, Users, FlagCheckered, Sparkle,
} from "@phosphor-icons/react";

// Map brief content types to app routes
const ROUTE_MAP = {
  "Newsletter": "/newsletter",
  "Lead Magnet": "/lead-magnet",
  "Email Sequence": "/sequence",
};

// Map brief content types to GeneratePage content types
const CONTENT_TYPE_MAP = {
  "Newsletter": "Newsletter Issue",
  "LinkedIn Post": "Thought Leadership",
  "Lead Magnet": "Lead Magnet Intro",
  "Instagram Post": "Conversion Post",
  "Blog Post": "Blog Post",
  "Email Sequence": "Email Sequence (Multi-Part)",
  "Facebook Post": "Conversion Post",
  "Twitter/X Thread": "Thought Leadership",
  "YouTube Script": "Long-Form Video Script",
  "Poll": "Poll / Engagement Post",
};

const TONE_CHIPS = [
  "Direct", "Sharp", "Calm", "Contrarian", "Insightful", "Practical", "Urgent",
  "Warm", "Provocative", "Technical", "Empathetic", "Witty", "Authoritative", "Conversational",
];

export default function BriefReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  // Editable config fields
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [suggestedAngle, setSuggestedAngle] = useState("");

  useEffect(() => {
    const fetchBrief = async () => {
      try {
        const resp = await api.get(`/briefs/${id}`);
        setBrief(resp.data);
        const cfg = resp.data.generated_form_config || {};
        setTopic(cfg.topic || "");
        setTone(cfg.tone || "");
        setCustomInstructions(cfg.custom_instructions || "");
        setSuggestedAngle(cfg.suggested_angle || "");
      } catch {
        toast.error("Brief not found");
        navigate("/briefs");
      }
      setLoading(false);
    };
    fetchBrief();
  }, [api, id, navigate]);

  const handleRegenerateAngle = async () => {
    if (!brief) return;
    setRegenerating(true);
    try {
      const resp = await api.post("/briefs/regenerate-angle", {
        brief_id: brief.brief_id,
        strategy_profile_id: brief.strategy_profile_id || null,
        business_id: brief.business_id,
      });
      setSuggestedAngle(resp.data.suggested_angle);
      setTopic(resp.data.topic);
      toast.success("New angle generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not regenerate angle");
    }
    setRegenerating(false);
  };

  const handleGenerate = () => {
    if (!brief) return;
    const cfg = brief.generated_form_config || {};
    const briefContentType = cfg.content_type || brief.answers?.content_type || "";

    // Determine route
    const route = ROUTE_MAP[briefContentType] || "/generate";

    // Map to GeneratePage content type
    const mappedContentType = CONTENT_TYPE_MAP[briefContentType] || briefContentType;

    // Build prefill
    const prefill = {
      topic,
      content_type: mappedContentType,
      platform: cfg.platform || brief.answers?.platform || "",
      audience: brief.answers?.audience || "",
      goal: brief.answers?.goal || "",
      tone_variations: tone ? [tone.split(" ")[0]] : [],
      research_text: customInstructions,
      brief_id: brief.brief_id,
      strategy_profile_id: brief.strategy_profile_id || null,
    };

    navigate(route, { state: { prefill, brief_id: brief.brief_id } });
  };

  const handleSaveForLater = async () => {
    toast.success("Brief saved! Find it in your Briefs list.");
    navigate("/briefs");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Header />
        <div className="flex justify-center py-20"><SpinnerGap size={32} className="animate-spin text-[#FF4400]" /></div>
      </div>
    );
  }

  if (!brief) return null;

  const answers = brief.answers || {};

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <button onClick={() => navigate("/briefs")} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-6" data-testid="back-to-briefs">
          <ArrowLeft size={16} /> All Briefs
        </button>

        <h1 className="text-2xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond'] mb-6" data-testid="brief-review-title">
          {brief.title}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column — Your Brief (read-only) */}
          <div className="space-y-4" data-testid="brief-answers-col">
            <h2 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Your Brief</h2>

            <ReadOnlyCard icon={Target} label="Goal" value={answers.goal} testId="brief-goal" />
            <ReadOnlyCard icon={Users} label="Audience" value={answers.audience} testId="brief-audience" />
            <ReadOnlyCard icon={FlagCheckered} label="Desired Outcome" value={answers.desired_outcome} testId="brief-outcome" />

            {answers.additional_context && (
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Additional Context</span>
                <p className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">{answers.additional_context}</p>
              </div>
            )}
          </div>

          {/* Right column — Your Content Strategy (editable) */}
          <div className="space-y-4" data-testid="brief-strategy-col">
            <h2 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Your Content Strategy</h2>

            {/* Suggested Angle (prominent) */}
            <div className="bg-[#FF4400]/5 border border-[#FF4400]/20 rounded-lg p-4 space-y-2" data-testid="brief-angle-box">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium text-[#FF4400] uppercase tracking-wider">Suggested Angle</span>
                <button onClick={handleRegenerateAngle} disabled={regenerating} className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[#FF4400] transition-colors disabled:opacity-40" data-testid="regenerate-angle-btn">
                  <ArrowsClockwise size={12} className={regenerating ? "animate-spin" : ""} /> Regenerate
                </button>
              </div>
              <p className="text-sm text-[var(--text-primary)] font-medium leading-relaxed" data-testid="suggested-angle-text">{suggestedAngle}</p>
            </div>

            {/* Topic */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider">Topic</label>
              <Input value={topic} onChange={e => setTopic(e.target.value)} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="brief-topic-input" />
            </div>

            {/* Tone */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider">Tone</label>
              <div className="flex flex-wrap gap-1.5">
                {TONE_CHIPS.map(t => (
                  <button key={t} onClick={() => setTone(tone === t ? "" : t)} className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${tone === t ? "bg-[#FF4400]/10 text-[#FF4400] border-[#FF4400]/30" : "bg-[var(--bg-card)] text-[var(--text-muted)] border-[var(--border)] hover:text-[var(--text-secondary)]"}`} data-testid={`review-tone-${t.toLowerCase()}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Platform & Content Type (display) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-3">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Platform</span>
                <p className="text-sm text-[var(--text-secondary)]" data-testid="brief-platform">{brief.generated_form_config?.platform || "—"}</p>
              </div>
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-3">
                <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Content Type</span>
                <p className="text-sm text-[var(--text-secondary)]" data-testid="brief-content-type">{brief.generated_form_config?.content_type || "—"}</p>
              </div>
            </div>

            {/* Custom Instructions */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider">Custom Instructions</label>
              <Textarea value={customInstructions} onChange={e => setCustomInstructions(e.target.value)} rows={3} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y" data-testid="brief-instructions-textarea" />
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between gap-3 pt-8 mt-8 border-t border-[var(--border)] flex-wrap" data-testid="brief-review-actions">
          <button onClick={() => { navigate("/briefs/new"); }} className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="start-over-link">
            Start Over
          </button>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleSaveForLater} className="border-[var(--border)] text-[var(--text-secondary)]" data-testid="save-brief-later-btn">
              <FloppyDisk size={16} className="mr-1.5" /> Save Brief for Later
            </Button>
            <Button onClick={handleGenerate} className="bg-[#FF4400] hover:bg-[#FF6633] text-white px-6" data-testid="generate-from-brief-btn">
              <Lightning size={16} weight="fill" className="mr-1.5" /> Generate Content with This Brief
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function ReadOnlyCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} className="text-[var(--text-muted)]" />
        <span className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">{value || "—"}</p>
    </div>
  );
}
